import { defineStore } from 'pinia';
import { ref } from 'vue';
import { generateUUID } from '@/lib/utils';

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';

export interface ToastAction {
    label: string;
    onClick: () => void;
}

export interface Toast {
    id: string;
    type: ToastType;
    title: string;
    message?: string;
    duration: number;
    action?: ToastAction;
    dismissible: boolean;
    persistent: boolean;
    color?: string;
}

export interface ToastOptions {
    id?: string;
    type?: ToastType;
    title: string;
    message?: string;
    duration?: number;
    action?: ToastAction;
    dismissible?: boolean;
    persistent?: boolean;
    color?: string;
}

const MAX_TOASTS = 5;

const DEFAULT_DURATIONS: Record<ToastType, number> = {
    success: 5000,
    info: 5000,
    warning: 7000,
    error: 10000,
    loading: 0,
};

/**
 * UI Pinia store for managing global UI state.
 *
 * Handles:
 * - Sidebar state (collapsed/expanded)
 * - Global loading states
 * - Toast notifications
 * - Modal state
 */
export const useUiStore = defineStore(
    'ui',
    () => {
        // Sidebar state
        const sidebarCollapsed = ref(false);
        const sidebarMobileOpen = ref(false);

        // Global loading
        const isGlobalLoading = ref(false);
        const loadingMessage = ref<string | null>(null);

        // Toast notifications
        const toasts = ref<Toast[]>([]);

        // Modal state
        const activeModal = ref<string | null>(null);
        const modalData = ref<Record<string, unknown>>({});

        // Sidebar actions
        function toggleSidebar() {
            sidebarCollapsed.value = !sidebarCollapsed.value;
        }

        function setSidebarCollapsed(collapsed: boolean) {
            sidebarCollapsed.value = collapsed;
        }

        function toggleMobileSidebar() {
            sidebarMobileOpen.value = !sidebarMobileOpen.value;
        }

        function closeMobileSidebar() {
            sidebarMobileOpen.value = false;
        }

        // Loading actions
        function setGlobalLoading(loading: boolean, message?: string) {
            isGlobalLoading.value = loading;
            loadingMessage.value = message || null;
        }

        function startLoading(message?: string) {
            setGlobalLoading(true, message);
        }

        function stopLoading() {
            setGlobalLoading(false);
        }

        // Toast actions
        function addToast(options: ToastOptions): string {
            const id = options.id || generateUUID();
            const type = options.type ?? 'info';
            const baseDuration = DEFAULT_DURATIONS[type];
            const duration =
                options.duration ??
                (baseDuration > 0 && options.action
                    ? baseDuration + 3000
                    : baseDuration);

            const newToast: Toast = {
                id,
                type,
                title: options.title,
                message: options.message,
                duration,
                action: options.action,
                dismissible: options.dismissible ?? true,
                persistent: options.persistent ?? false,
                color: options.color,
            };

            // Enforce max visible toasts
            while (toasts.value.length >= MAX_TOASTS) {
                toasts.value.shift();
            }

            toasts.value.push(newToast);

            return id;
        }

        function updateToast(
            id: string,
            updates: Partial<Omit<Toast, 'id'>>
        ) {
            const index = toasts.value.findIndex((t) => t.id === id);
            if (index !== -1) {
                toasts.value[index] = { ...toasts.value[index], ...updates };
            }
        }

        function removeToast(id: string) {
            const index = toasts.value.findIndex((t) => t.id === id);
            if (index !== -1) {
                toasts.value.splice(index, 1);
            }
        }

        function clearToasts() {
            toasts.value = [];
        }

        // Convenience toast methods
        function showSuccess(title: string, message?: string) {
            return addToast({ type: 'success', title, message });
        }

        function showError(title: string, message?: string) {
            return addToast({ type: 'error', title, message });
        }

        function showWarning(title: string, message?: string) {
            return addToast({ type: 'warning', title, message });
        }

        function showInfo(title: string, message?: string) {
            return addToast({ type: 'info', title, message });
        }

        /**
         * Show a toast that tracks a promise lifecycle:
         * loading → success/error.
         */
        async function promise<T>(
            promiseOrFn: Promise<T> | (() => Promise<T>),
            messages: {
                loading: string;
                success: string | ((data: T) => string);
                error: string | ((err: unknown) => string);
            }
        ): Promise<T> {
            const id = addToast({
                type: 'loading',
                title: messages.loading,
                persistent: true,
                dismissible: false,
            });

            try {
                const result = await (typeof promiseOrFn === 'function'
                    ? promiseOrFn()
                    : promiseOrFn);

                updateToast(id, {
                    type: 'success',
                    title:
                        typeof messages.success === 'function'
                            ? messages.success(result)
                            : messages.success,
                    persistent: false,
                    dismissible: true,
                    duration: DEFAULT_DURATIONS.success,
                });

                return result;
            } catch (err) {
                updateToast(id, {
                    type: 'error',
                    title:
                        typeof messages.error === 'function'
                            ? messages.error(err)
                            : messages.error,
                    persistent: false,
                    dismissible: true,
                    duration: DEFAULT_DURATIONS.error,
                });

                throw err;
            }
        }

        // Modal actions
        function openModal(name: string, data?: Record<string, unknown>) {
            activeModal.value = name;
            modalData.value = data || {};
        }

        function closeModal() {
            activeModal.value = null;
            modalData.value = {};
        }

        function isModalOpen(name: string): boolean {
            return activeModal.value === name;
        }

        return {
            // State
            sidebarCollapsed,
            sidebarMobileOpen,
            isGlobalLoading,
            loadingMessage,
            toasts,
            activeModal,
            modalData,

            // Sidebar actions
            toggleSidebar,
            setSidebarCollapsed,
            toggleMobileSidebar,
            closeMobileSidebar,

            // Loading actions
            setGlobalLoading,
            startLoading,
            stopLoading,

            // Toast actions
            addToast,
            updateToast,
            removeToast,
            clearToasts,
            showSuccess,
            showError,
            showWarning,
            showInfo,
            promise,

            // Modal actions
            openModal,
            closeModal,
            isModalOpen,
        };
    },
    {
        persist: {
            key: 'fnl-ui',
            paths: ['sidebarCollapsed'],
        },
    }
);

export type UiStore = ReturnType<typeof useUiStore>;
